// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import { Filter, Plus, Search, SquarePen, Trash2, X } from "lucide-react";
import { userService } from "../../services/userService";
import { roleService } from "../../services/roleService";
import tenantService from "../../services/tenantService";
import DataTable from "react-data-table-component";
import LoadingOverlay from "../LoadingOverlay";
import InlineNotification from "../InlineNotification";
import useInlineNotification from "../../hooks/useInlineNotification";
import { usePermissions } from "../../hooks/usePermissions";
import { useTheme } from "../../context/ThemeContext";
import GlobalAdminHeader from "./GlobalAdminHeader";

const UserList = () => {
  const { isDarkMode } = useTheme();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    role: "",
    status: "",
  });
  const [newUser, setNewUser] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    roles: "",
    status: "",
  });
  const [roles, setRoles] = useState([]);
  const [tenants, setTenants] = useState([]);
  const statusOptions = [
    { value: "active", label: "Aktiv" },
    { value: "inactive", label: "Inaktiv" },
    { value: "pending", label: "Ausstehend" },
    { value: "suspended", label: "Gesperrt" },
    { value: "deleted", label: "Gelöscht" },
  ];
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { notification, showSuccess, showError } = useInlineNotification();
  const { userPermissions } = usePermissions();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [userResponse, rolesResponse, tenantsResponse] =
          await Promise.all([
            userService.getTenantUsers(),
            roleService.getRoles(),
            tenantService.getAllTenantsForFilter({
              includeAll: false,
              limit: 500,
            }),
          ]);
        const users = userResponse.users || userResponse || [];
        setData(users);
        const rolesData = rolesResponse.items || rolesResponse || [];
        setRoles(rolesData);
        setTenants(Array.isArray(tenantsResponse) ? tenantsResponse : []);
      } catch (error) {
        console.error("Error fetching data:", error);
        setData([]);
        setRoles([]);
        setTenants([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (roles.length > 0 && !newUser.roles[0]) {
      const defaultRole = roles.find((role) => role.name === "") || roles[0];
      if (defaultRole) {
        setNewUser((prev) => ({
          ...prev,
          roles: [defaultRole.name],
        }));
      }
    }
  }, [roles]);

  const handlePostUser = async (userData) => {
    try {
      const response = await userService.postTenantUser(userData);
      return response;
    } catch (error) {
      console.error("Error posting user:", error);
      throw error;
    }
  };

  const handlePutUser = async (userId, userData) => {
    try {
      const response = await userService.putTenantUser(userId, userData);
      return response;
    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    }
  };
  const formatDateTime = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d)) return null;
    return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
  };
  // Filter data based on search query and filters
  const filteredData = data.filter((user) => {
    const matchesSearch =
      (user.first_name &&
        user.first_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (user.last_name &&
        user.last_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (user.email &&
        user.email.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesRole =
      !filters.role || (user.roles && user.roles.includes(filters.role));
    const matchesStatus = !filters.status || user.status === filters.status;

    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleCloseModal = () => {
    setShowAddUserModal(false);
    setNewUser({
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      roles: "",
      status: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (
      !newUser.first_name ||
      !newUser.last_name ||
      !newUser.email ||
      !newUser.roles[0] ||
      !newUser.status
    ) {
      showError("Bitte füllen Sie alle Pflichtfelder aus.");
      setIsSubmitting(false);
      return;
    }

    const userToAdd = {
      first_name: newUser.first_name.trim(),
      last_name: newUser.last_name.trim(),
      email: newUser.email.trim().toLowerCase(),
      roles: Array.isArray(newUser.roles) ? newUser.roles : [newUser.roles],
      status: newUser.status,
    };

    try {
      const response = await handlePostUser(userToAdd);
      setData((prev) => [response, ...prev]);
      showSuccess("Nutzer erfolgreich erstellt!");
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    } catch (error) {
      console.error("Error creating user:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        headers: error.response?.headers,
        config: error.config,
      });

      let errorMessage = "Fehler beim Erstellen des Benutzers";

      if (error.response?.status === 422) {
        const validationErrors = error.response?.data;
        if (validationErrors?.detail) {
          if (Array.isArray(validationErrors.detail)) {
            const fieldErrors = validationErrors.detail
              .map((err) => `${err.loc?.join(".")}: ${err.msg}`)
              .join(", ");
            errorMessage = `Validierungsfehler: ${fieldErrors}`;
          } else {
            errorMessage = `Validierungsfehler: ${validationErrors.detail}`;
          }
        } else if (validationErrors?.message) {
          errorMessage = validationErrors.message;
        } else if (typeof validationErrors === "string") {
          errorMessage = validationErrors;
        } else if (validationErrors) {
          const errorKeys = Object.keys(validationErrors);
          if (errorKeys.length > 0) {
            const fieldErrors = errorKeys
              .map((key) => `${key}: ${validationErrors[key]}`)
              .join(", ");
            errorMessage = `Validierungsfehler: ${fieldErrors}`;
          }
        }
      } else if (error.response?.status === 409) {
        errorMessage =
          "Diese E-Mail-Adresse wird bereits verwendet. Bitte verwenden Sie eine andere E-Mail-Adresse.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          const emailError = detail.find(
            (err) =>
              err.loc?.includes("email") ||
              err.msg?.toLowerCase().includes("email") ||
              err.msg?.toLowerCase().includes("already exists") ||
              err.msg?.toLowerCase().includes("duplicate"),
          );
          if (emailError) {
            errorMessage =
              "Diese E-Mail-Adresse wird bereits verwendet. Bitte verwenden Sie eine andere E-Mail-Adresse.";
          }
        } else if (typeof detail === "string") {
          if (
            detail.toLowerCase().includes("email") ||
            detail.toLowerCase().includes("already exists") ||
            detail.toLowerCase().includes("duplicate")
          ) {
            errorMessage =
              "Diese E-Mail-Adresse wird bereits verwendet. Bitte verwenden Sie eine andere E-Mail-Adresse.";
          }
        }
      }

      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditUser = (user) => {
    const editingUserData = {
      user_id: user.user_id,
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      password: "",
      roles: Array.isArray(user.roles)
        ? user.roles
        : [user.roles].filter(Boolean),
      status: user.status || "",
      tenant_id:
        user.tenant_id ||
        user.tenantId ||
        user.tenant?.id ||
        user.tenant?.tenant_id ||
        "",
      tenant_name:
        user.tenant_name || user.tenant?.name || user.tenant?.tenant_name || "",
    };

    setEditingUser(editingUserData);
    setShowEditUserModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditUserModal(false);
    setEditingUser(null);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditingUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const editingUserId = editingUser.user_id || editingUser.id;
      const selectedTenantIdRaw = editingUser.tenant_id;
      const selectedTenantId =
        selectedTenantIdRaw === "" || selectedTenantIdRaw == null
          ? null
          : String(selectedTenantIdRaw);
      const selectedTenant = tenants.find(
        (tenant) => String(tenant.id) === String(editingUser.tenant_id),
      );

      const userData = {
        first_name: editingUser.first_name,
        last_name: editingUser.last_name,
        email: editingUser.email,
        roles: editingUser.roles,
        status: editingUser.status,
        tenant_id: selectedTenantId,
      };

      if (editingUser.password && editingUser.password.trim()) {
        userData.password = editingUser.password.trim();
      }

      const response = await handlePutUser(editingUserId, userData);
      const updatedUser = response?.user || response?.data || response;
      const updatedUserId =
        updatedUser?.user_id || updatedUser?.id || editingUserId;

      setData((prev) =>
        prev.map((user) =>
          (user.user_id || user.id) === editingUserId
            ? {
                ...user,
                ...updatedUser,
                user_id: updatedUserId,
                tenant_id: updatedUser?.tenant_id ?? selectedTenantId,
                tenant_name:
                  updatedUser?.tenant_name ||
                  selectedTenant?.name ||
                  user.tenant_name,
              }
            : user,
        ),
      );
      showSuccess("Nutzer erfolgreich aktualisiert!");
      handleCloseEditModal();
    } catch (error) {
      console.error("Error updating user:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
      });
      showError("Fehler beim Aktualisieren des Nutzers.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirmModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteConfirmModal(false);
    setUserToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (userToDelete) {
      try {
        await userService.deleteTenantUser(userToDelete.user_id);
        setData((prev) =>
          prev.filter((user) => user.user_id !== userToDelete.user_id),
        );
        showSuccess("Nutzer erfolgreich gelöscht!");
        handleCloseDeleteModal();
      } catch (error) {
        console.error("Error deleting user:", error);
        console.error("Error details:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          statusText: error.response?.statusText,
          headers: error.response?.headers,
          config: error.config,
        });

        let errorMessage =
          "Fehler beim Löschen des Benutzers. Bitte versuchen Sie es erneut.";

        if (error.response?.status === 404) {
          errorMessage =
            "Benutzer nicht gefunden oder Lösch-Endpunkt nicht verfügbar.";
        } else if (error.response?.status === 403) {
          errorMessage = "Keine Berechtigung zum Löschen von Benutzern.";
        } else if (error.response?.status === 409) {
          errorMessage =
            "Benutzer kann nicht gelöscht werden (möglicherweise noch in Verwendung).";
        } else if (error.response?.status === 500) {
          errorMessage = "Server-Fehler beim Löschen des Benutzers.";
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        showError(errorMessage);
      }
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      role: "",
      status: "",
    });
  };

  const handleApplyFilters = () => {
    setShowFilterModal(false);
  };

  const handleCloseFilterModal = () => {
    setShowFilterModal(false);
  };

  const getStatusLabel = (statusValue) => {
    const statusOption = statusOptions.find(
      (status) => status.value === statusValue,
    );
    return statusOption ? statusOption.label : statusValue;
  };

  // Create columns with permission checks
  const columns = [
    {
      id: "name",
      name: "Name",
      selector: (row) => `${row.first_name} ${row.last_name}`,
      sortable: true,
      cell: (row) => (
        <div className="font-medium text-gray-900 dark:text-slate-200 capitalize">
          {row.first_name} {row.last_name}
        </div>
      ),
    },
    {
      id: "email",
      name: "E-Mail",
      selector: (row) => row.email,
      sortable: true,
      cell: (row) => (
        <div className="text-gray-500 dark:text-slate-400 whitespace-nowrap">
          {row.email}
        </div>
      ),
    },
    {
      id: "roles",
      name: "Rolle",
      selector: (row) => row.roles?.join(", ") || "",
      sortable: true,
      cell: (row) => (
        <div className="flex gap-1 capitalize">
          {row?.roles && row.roles.length > 0 ? (
            <span
              className={`px-2 py-1 text-xs font-medium rounded-full ${
                row.roles.includes("super_admin")
                  ? "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300"
                  : row.roles.includes("admin")
                    ? "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300"
                    : row.roles.includes("manager")
                      ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300"
                      : "bg-primary-color-tint primary-color"
              }`}
            >
              {`${row.roles[0]}${row.roles.length > 1 ? "…" : ""}`}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      id: "tenant",
      name: "Tenant",
      selector: (row) => row.tenant_name,
      sortable: true,
      cell: (row) =>
        row.tenant_name ? (
          <div className="text-gray-500 dark:text-slate-400 whitespace-nowrap">
            {row.tenant_name}
          </div>
        ) : (
          <div className="text-gray-500 dark:text-slate-400 whitespace-nowrap">
            -
          </div>
        ),
    },
    {
      id: "email_verified",
      name: "Email verified",
      selector: (row) => row.email_verified,
      sortable: true,
      cell: (row) =>
        row.email_verified ? (
          <div className="text-gray-500 dark:text-slate-400 whitespace-nowrap">
            {row.email_verified ? "Ja" : "Nein"}
          </div>
        ) : (
          <div className="text-gray-500 dark:text-slate-400 whitespace-nowrap">
            -
          </div>
        ),
    },
    {
      id: "status",
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => (
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${
            row.status === "deleted"
              ? "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300"
              : row.status === "inactive" || row.status === "suspended"
                ? "bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300"
                : row.status === "pending"
                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300"
                  : "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
          }`}
        >
          {getStatusLabel(row.status)}
        </span>
      ),
    },
    {
      id: "created_at",
      name: "Created at",
      selector: (row) => formatDateTime(row.created_at),
      sortable: true,
      cell: (row) => (
        <div className="text-gray-500 dark:text-slate-400 whitespace-nowrap">
          {formatDateTime(row.created_at)}
        </div>
      ),
    },
    {
      id: "updated_at",
      name: "Updated at",
      selector: (row) => formatDateTime(row.updated_at),
      sortable: true,
      cell: (row) => (
        <div className="text-gray-500 dark:text-slate-400 whitespace-nowrap">
          {formatDateTime(row.updated_at)}
        </div>
      ),
    },
    // Only show actions column if user has update or delete permissions
    ...(userPermissions?.includes("user.update") ||
    userPermissions?.includes("user.delete")
      ? [
          {
            id: "actions",
            name: "Aktionen",
            selector: (row) => row.user_id,
            sortable: false,
            cell: (row) => (
              <div className="flex items-center space-x-3">
                {userPermissions?.includes("user.update") && (
                  <button
                    onClick={() => handleEditUser(row)}
                    className="primary-color hover:opacity-80"
                    title="Bearbeiten"
                  >
                    <SquarePen className="w-4 h-4" />
                  </button>
                )}
                {userPermissions?.includes("user.delete") && (
                  <button
                    onClick={() => handleDeleteUser(row)}
                    className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                    title="Löschen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="relative h-full">
      <div className="flex gap-4 h-full">
        <div
          className="flex flex-col w-full"
          style={{ width: showEditUserModal ? "calc(100% - 415px)" : "100%" }}
        >
          <GlobalAdminHeader
            title="Nutzer"
            buttons={
              userPermissions?.includes("user.create")
                ? [
                    {
                      text: "Nutzer hinzufügen",
                      icon: <Plus className="w-4 h-4" />,
                      className:
                        "flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-color text-white font-medium shadow-lg hover:opacity-90 transition-opacity",
                      onClick: () => setShowAddUserModal(true),
                    },
                  ]
                : []
            }
          />

          <div className="mx-auto px-4 lg:px-4 sm:mt-6 mt-3 pb-6 w-full">
            <InlineNotification notification={notification} />
            {/* Toolbar: search + filter */}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-4">
              <div className="flex-1 w-full sm:max-w-md relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-slate-500 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Suche Nutzer..."
                  value={searchQuery}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent dark:placeholder-slate-400"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowFilterModal(true)}
                className="flex items-center px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 dark:text-slate-200 shrink-0"
              >
                <Filter className="w-5 h-5 mr-2" />
                Filter
                {(filters.role || filters.status) && (
                  <span className="ml-2 px-2 py-1 text-xs bg-primary-color-tint primary-color rounded-full">
                    {[filters.role, filters.status].filter(Boolean).length}
                  </span>
                )}
              </button>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 overflow-hidden">
              {loading ? (
                <LoadingOverlay loading={loading} />
              ) : (
                <>
                  <DataTable
                    columns={columns}
                    data={filteredData}
                    keyField="user_id"
                    className="table-title-style"
                    selectableRows={false}
                    pagination
                    paginationPerPage={25}
                    paginationRowsPerPageOptions={[25, 50, 75, 100]}
                    noDataComponent={
                      <span className="text-gray-500 dark:text-slate-400">
                        Keine Nutzer gefunden
                      </span>
                    }
                    progressPending={loading}
                    highlightOnHover
                    pointerOnHover
                    conditionalRowStyles={[
                      {
                        when: (row) =>
                          !!editingUser &&
                          (row.user_id || row.id) ===
                            (editingUser.user_id || editingUser.id),
                        style: {
                          backgroundColor:
                            "color-mix(in srgb, var(--primary-color) 12%, transparent)",
                          borderLeft: "3px solid var(--primary-color)",
                          fontWeight: 500,
                        },
                        classNames: ["row-edit-selected"],
                      },
                    ]}
                    customStyles={{
                      table: {
                        style: {
                          backgroundColor: isDarkMode ? "#1e293b" : undefined,
                        },
                      },
                      headRow: {
                        style: {
                          backgroundColor: isDarkMode ? "#334155" : undefined,
                          borderBottom: isDarkMode
                            ? "1px solid #475569"
                            : undefined,
                        },
                      },
                      headCells: {
                        style: {
                          color: isDarkMode ? "#94a3b8" : undefined,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        },
                      },
                      rows: {
                        style: {
                          backgroundColor: isDarkMode ? "#1e293b" : undefined,
                          "&:hover": {
                            backgroundColor: isDarkMode ? "#334155" : undefined,
                          },
                        },
                      },
                      cells: {
                        style: {
                          color: isDarkMode ? "#e2e8f0" : undefined,
                          backgroundColor: isDarkMode ? "#1e293b" : undefined,
                        },
                      },
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Edit User - right-side panel (like Products) */}
        {showEditUserModal &&
          editingUser &&
          userPermissions?.includes("user.update") && (
            <div
              className="absolute right-0 top-0 h-full"
              style={{ width: "415px" }}
            >
              <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 h-full overflow-hidden flex flex-col">
                <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50 shrink-0">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                    Nutzer bearbeiten
                  </h3>
                  <button
                    type="button"
                    onClick={handleCloseEditModal}
                    className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 transition-colors p-2"
                    disabled={isSubmitting}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form
                  onSubmit={handleSubmitEdit}
                  className="p-4 space-y-4 overflow-y-auto flex-1"
                  style={{ scrollbarWidth: "thin" }}
                >
                  <div className="form-row">
                    <div className="form-group">
                      <label
                        htmlFor="edit-first_name"
                        className="block font-medium text-gray-700 dark:text-slate-300 mb-1"
                      >
                        Vorname
                      </label>
                      <input
                        type="text"
                        id="edit-first_name"
                        name="first_name"
                        value={editingUser.first_name}
                        onChange={handleEditInputChange}
                        required
                        disabled={isSubmitting}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-slate-600 disabled:cursor-not-allowed dark:placeholder-slate-400"
                        placeholder="Vorname eingeben"
                      />
                    </div>

                    <div className="form-group">
                      <label
                        htmlFor="edit-last_name"
                        className="block font-medium text-gray-700 dark:text-slate-300 mb-1"
                      >
                        Nachname
                      </label>
                      <input
                        type="text"
                        id="edit-last_name"
                        name="last_name"
                        value={editingUser.last_name}
                        onChange={handleEditInputChange}
                        required
                        disabled={isSubmitting}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-slate-600 disabled:cursor-not-allowed dark:placeholder-slate-400"
                        placeholder="Nachname eingeben"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label
                      htmlFor="edit-email"
                      className="block font-medium text-gray-700 dark:text-slate-300 mb-1"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="edit-email"
                      name="email"
                      value={editingUser.email}
                      onChange={handleEditInputChange}
                      autoComplete="off"
                      required
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-slate-600 disabled:cursor-not-allowed dark:placeholder-slate-400"
                      placeholder="Email-Adresse eingeben"
                    />
                  </div>
                  <div className="form-row pb-4">
                    <div className="form-group">
                      <label
                        htmlFor="edit-roles"
                        className="block font-medium text-gray-700 dark:text-slate-300 mb-1"
                      >
                        Rolle
                      </label>
                      <select
                        id="edit-role"
                        name="role"
                        value={editingUser.roles?.[0] || ""}
                        onChange={(e) =>
                          setEditingUser((prev) => ({
                            ...prev,
                            roles: [e.target.value],
                          }))
                        }
                        required
                        disabled={isSubmitting}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent capitalize disabled:bg-gray-100 dark:disabled:bg-slate-600 disabled:cursor-not-allowed"
                      >
                        <option key="edit-role-placeholder" value="">
                          Rolle auswählen
                        </option>
                        {roles.map((role, index) => (
                          <option
                            key={role.id || role.name || `role-${index}`}
                            value={role.name}
                          >
                            {role.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label
                        htmlFor="edit-tenant_id"
                        className="block font-medium text-gray-700 dark:text-slate-300 mb-1"
                      >
                        Mandant
                      </label>
                      <select
                        id="edit-tenant_id"
                        name="tenant_id"
                        value={
                          editingUser.tenant_id == null
                            ? ""
                            : String(editingUser.tenant_id)
                        }
                        onChange={handleEditInputChange}
                        disabled={isSubmitting}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-slate-600 disabled:cursor-not-allowed dark:placeholder-slate-400"
                      >
                        <option value="">Kein Mandant</option>
                        {tenants.map((tenant) => (
                          <option key={tenant.id} value={String(tenant.id)}>
                            {tenant.name}
                          </option>
                        ))}
                        {editingUser.tenant_id &&
                          !tenants.some(
                            (t) =>
                              String(t.id) === String(editingUser.tenant_id),
                          ) && (
                            <option value={String(editingUser.tenant_id)}>
                              {editingUser.tenant_name ||
                                `Mandant ${editingUser.tenant_id}`}
                            </option>
                          )}
                      </select>
                    </div>

                    <div className="form-group">
                      <label
                        htmlFor="edit-status"
                        className="block font-medium text-gray-700 dark:text-slate-300 mb-1"
                      >
                        Status
                      </label>
                      <select
                        id="edit-status"
                        name="status"
                        value={editingUser.status}
                        onChange={handleEditInputChange}
                        required
                        disabled={isSubmitting}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-slate-600 disabled:cursor-not-allowed dark:placeholder-slate-400"
                      >
                        <option key="edit-status-placeholder" value="">
                          Status auswählen
                        </option>
                        {statusOptions.map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={handleCloseEditModal}
                      disabled={isSubmitting}
                      className="flex items-center px-4 py-2 text-sm bg-gray-500 dark:bg-slate-600 text-white rounded-lg hover:bg-gray-600 dark:hover:bg-slate-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex items-center px-4 py-2 text-sm bg-primary-color text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Wird aktualisiert...
                        </>
                      ) : (
                        "Nutzer aktualisieren"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
      </div>

      {/* Only show modals if user has appropriate permissions */}
      {showAddUserModal && userPermissions?.includes("user.create") && (
        <div className="modal-overlay active" onClick={handleCloseModal}>
          <div
            className="modal-container dark:bg-slate-800 dark:border-slate-700"
            style={{ maxWidth: "480px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-slate-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                Nutzer hinzufügen
              </h3>
              <button
                type="button"
                onClick={handleCloseModal}
                aria-label="Schließen"
                className="p-1.5 -mr-1.5 rounded-lg text-gray-400 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={handleSubmitUser}
              className="p-6 space-y-4 max-h-[90dvh] overflow-auto"
            >
              <div className="form-row">
                <div className="form-group">
                  <label
                    htmlFor="first_name"
                    className="block font-medium text-gray-700 dark:text-slate-300 mb-1"
                  >
                    Vorname
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={newUser.first_name}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-slate-600 disabled:cursor-not-allowed dark:placeholder-slate-400"
                    placeholder="Vorname eingeben"
                  />
                </div>

                <div className="form-group">
                  <label
                    htmlFor="last_name"
                    className="block font-medium text-gray-700 dark:text-slate-300 mb-1"
                  >
                    Nachname
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={newUser.last_name}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-slate-600 disabled:cursor-not-allowed dark:placeholder-slate-400"
                    placeholder="Nachname eingeben"
                  />
                </div>
              </div>
              <div className="form-group">
                <label
                  htmlFor="email"
                  className="block font-medium text-gray-700 dark:text-slate-300 mb-1"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={newUser.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-slate-600 disabled:cursor-not-allowed dark:placeholder-slate-400"
                  placeholder="Email-Adresse eingeben"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label
                    htmlFor="roles"
                    className="block font-medium text-gray-700 dark:text-slate-300 mb-1"
                  >
                    Rolle
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={newUser.roles?.[0] || ""}
                    onChange={(e) =>
                      setNewUser((prev) => ({
                        ...prev,
                        roles: [e.target.value],
                      }))
                    }
                    required
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent capitalize disabled:bg-gray-100 dark:disabled:bg-slate-600 disabled:cursor-not-allowed"
                  >
                    <option key="add-role-placeholder" value="">
                      Rolle auswählen
                    </option>
                    {roles.map((role, index) => (
                      <option
                        key={role.id || role.name || `role-${index}`}
                        value={role.name}
                      >
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label
                    htmlFor="status"
                    className="block font-medium text-gray-700 dark:text-slate-300 mb-1"
                  >
                    Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={newUser.status}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-slate-600 disabled:cursor-not-allowed dark:placeholder-slate-400"
                  >
                    <option key="add-status-placeholder" value="">
                      Status auswählen
                    </option>
                    {statusOptions.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-4 py-2.5 bg-primary-color text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Wird erstellt...
                    </>
                  ) : (
                    "Nutzer hinzufügen"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal - only show if user has delete permission */}
      {showDeleteConfirmModal &&
        userToDelete &&
        userPermissions?.includes("user.delete") && (
          <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50">
            <div
              className="bg-white dark:bg-slate-800 rounded-lg shadow-xl dark:border dark:border-slate-700 w-full max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-slate-600">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                  Nutzer löschen
                </h3>
                <button
                  onClick={handleCloseDeleteModal}
                  className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <p className="text-gray-700 dark:text-slate-300">
                    Sind Sie sicher, dass Sie den Nutzer{" "}
                    <strong className="dark:text-slate-100">
                      {userToDelete.first_name} {userToDelete.last_name}
                    </strong>{" "}
                    löschen möchten?
                  </p>
                  <p className="text-gray-500 dark:text-slate-400 mt-2">
                    Diese Aktion kann nicht rückgängig gemacht werden.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCloseDeleteModal}
                    className="flex-1 px-4 py-2 border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-200 bg-rose-100 dark:bg-rose-900/30 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/50 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    className="flex-1 px-4 py-2 bg-red-500 dark:bg-red-600 text-white rounded-lg hover:bg-red-600 dark:hover:bg-red-500 transition-colors"
                  >
                    Nutzer löschen
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
          <div
            className="bg-white dark:bg-slate-800 rounded-lg shadow-xl dark:border dark:border-slate-700 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-slate-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                Nutzer filtern
              </h3>
              <button
                type="button"
                onClick={handleCloseFilterModal}
                className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label
                  htmlFor="filter-role"
                  className="block font-medium text-gray-700 dark:text-slate-300 mb-1"
                >
                  Rolle
                </label>
                <select
                  id="filter-role"
                  name="role"
                  value={filters.role}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent capitalize"
                >
                  <option key="filter-role-placeholder" value="">
                    Alle Rollen
                  </option>
                  {roles.map((role, index) => (
                    <option
                      key={role.id || role.name || `role-${index}`}
                      value={role.name}
                    >
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="filter-status"
                  className="block font-medium text-gray-700 dark:text-slate-300 mb-1"
                >
                  Status:
                </label>
                <select
                  id="filter-status"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent"
                >
                  <option key="filter-status-placeholder" value="">
                    Alle Status
                  </option>
                  {statusOptions.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="flex-1 px-4 py-2 border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-200 bg-rose-100 dark:bg-rose-900/30 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/50 transition-colors"
                >
                  Filter löschen
                </button>
                <button
                  type="button"
                  onClick={handleApplyFilters}
                  className="flex-1 px-4 py-2 bg-primary-color text-white rounded-lg hover:opacity-90 transition-colors"
                >
                  Filter anwenden
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
